/**
 * 草稿实时保存的纯逻辑：存完之后怎么合并本地状态、新建的行怎么拿回 id、一次保存改了什么。
 *
 * 实时保存意味着「请求在路上时人还在改」。保存结束后不能把服务端读回的快照
 * 整份盖回界面——那会吃掉这一两秒里的新输入。按块（快照的顶层键）合并：
 * 保存期间没再动的块用服务端的事实，动过的块留本地，下一轮再存。
 * 新建的行不靠「整块换服务端」拿 id（那会连带吃掉同一块里的其他改动），而是逐行套回执。
 */

type Snap = object;

/** 实时保存的状态，版本条右上角那行字由它决定 */
export type SaveState =
  | { state: "idle" }
  | { state: "saving" }
  | { state: "saved"; atMs: number; by: string }
  /** retry：网络类失败自动重试；服务端拒了的不重试，at 记下失败那一刻的工作副本，再改一笔才再试 */
  | { state: "error"; msg: string; attempt: number; retry: boolean; at: string }
  | { state: "conflict"; msg: string };

/**
 * 按内容比：对象的键顺序不算差别。同一行从服务端读回来与本地拼出来的，键的先后常常不同，
 * 按 JSON 原文比会把同一行认成「被改过」
 */
export function canon(x: unknown): string {
  return JSON.stringify(x ?? null, (_k, v) => (v && typeof v === "object" && !Array.isArray(v)
    ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, (v as Record<string, unknown>)[k]])) : v));
}
const same = (a: unknown, b: unknown) => canon(a) === canon(b);

export interface MergeInput<S extends Snap> {
  /** 这次保存发出去的本地快照（已套上建行回执） */
  sent: S;
  /** 保存结束这一刻的本地快照（保存期间可能又改了；已套上建行回执） */
  live: S;
  /** 保存结束后从服务端读回的快照 */
  server: S;
  /** 这次保存是否全部写成功 */
  ok: boolean;
}

/**
 * 成功：块在保存期间没再动 → 服务端的（规范化后的值、服务端字段）；动过 → 本地，下一轮再存。
 * 失败：一律本地。已经写进去的那部分靠新基线（服务端读回）在下一轮自然变成「无差异」，
 * 新建出来的行靠回执拿到了 id，下一轮走更新，不会再建一遍。
 */
export function mergeAfterSave<S extends Snap>({ sent, live, server, ok }: MergeInput<S>): S {
  if (!ok) return live;
  const out = { ...live } as S;
  for (const k of Object.keys(server) as Array<keyof S & string>) {
    if (same(live[k], sent[k])) out[k] = server[k];
  }
  return out;
}

/**
 * 建行回执：这一轮里服务端确认建出来的行。保存不论成败、冲突与否，都要先把回执
 * 套回本地，否则本地那份还是「没有 id / 未落库」，下一轮会再建一遍（重复行或撞主键）。
 */
export interface CreateReceipts {
  /** 拼图：本地 localKey → 服务端分配的 puzzleId 与序号 */
  puzzles: Map<string, { puzzleId: string; seq: number }>;
  /** 萌宠：编号是运营手填的，建成功就是落库了 */
  pets: Set<string>;
  /** 已插入成功的新关位置号（pendingAdds 里要去掉的） */
  levels: number[];
  /** idol 关卡相册 / VIP 照片 / VIP 视频：本地 localKey → 服务端分配的 ID（同拼图，只是没有序号） */
  idolPhotos: Map<string, string>;
  idolVipPhotos: Map<string, string>;
  idolVipVideos: Map<string, string>;
  /** 关卡多图（改版）：关卡 / 关卡图片，同上 */
  stages: Map<string, string>;
  stageImages: Map<string, string>;
}
export const emptyReceipts = (): CreateReceipts => ({
  puzzles: new Map(), pets: new Set(), levels: [], idolPhotos: new Map(), idolVipPhotos: new Map(), idolVipVideos: new Map(),
  stages: new Map(), stageImages: new Map(),
});

/**
 * 服务端分配 ID、本地键 = uid 的三块（相册 / VIP 照片 / VIP 视频）：块名 → ID 字段名。
 * 回执套回与回包丢失后的认领都按这张表走，三块一个口径
 */
export const ASSIGNED_ID_BLOCKS = [
  ["idolPhotos", "photoId"],
  ["idolVipPhotos", "vipPhotoId"],
  ["idolVipVideos", "vipVideoId"],
  ["stages", "stageId"],
  ["stageImages", "imageId"],
] as const;
type AssignedRow = { localKey: string } & Record<string, unknown>;
type AssignedBlocks = { idolPhotos?: unknown[]; idolVipPhotos?: unknown[]; idolVipVideos?: unknown[]; stages?: unknown[]; stageImages?: unknown[] };

/** 把回执套到一份快照上：只动 puzzles / pets / 相册三块里对得上号的行，其余原样 */
export function applyReceipts<S extends { puzzles?: Array<{ localKey: string; puzzleId: string | null; seq: number }>; pets?: Array<{ petId: string; persisted: boolean }> } & AssignedBlocks>(snap: S, r: CreateReceipts): S {
  const album = ASSIGNED_ID_BLOCKS.some(([b]) => r[b].size > 0);
  if (!r.puzzles.size && !r.pets.size && !album) return snap;
  const out: S = {
    ...snap,
    ...(snap.puzzles ? { puzzles: snap.puzzles.map((x) => (x.puzzleId == null && r.puzzles.has(x.localKey) ? { ...x, ...r.puzzles.get(x.localKey)! } : x)) } : {}),
    ...(snap.pets ? { pets: snap.pets.map((x) => (!x.persisted && r.pets.has(x.petId) ? { ...x, persisted: true } : x)) } : {}),
  };
  for (const [block, idKey] of ASSIGNED_ID_BLOCKS) {
    const rows = snap[block] as AssignedRow[] | undefined;
    const got = r[block];
    if (!rows || !got.size) continue;
    (out as AssignedBlocks)[block] = rows.map((x) => (x[idKey] == null && got.has(x.localKey) ? { ...x, [idKey]: got.get(x.localKey)! } : x));
  }
  return out;
}

export interface Change {
  /** 快照顶层键：levels / dice / pets … */
  block: string;
  /** 块内位置，数组元素按 id / key / code 认，认不出才用下标：`[3].buildings[1].actualC` */
  path: string;
  from?: unknown;
  to?: unknown;
  /** 整行新增 / 删除 */
  op?: "add" | "remove";
  /** 这一行叫什么（name / label / title），给修改记录写「第 3 关「cc」」用 */
  row?: string;
}

const ROW_IDS = ["id", "key", "code", "nodeId", "tier", "sourceKey", "modeId", "rivalId", "petId", "puzzleId", "packId", "rewardId", "photoId", "vipPhotoId", "vipVideoId", "imageId", "stageId", "seq", "level", "star"];
export function rowId(x: unknown): string | null {
  if (!x || typeof x !== "object" || Array.isArray(x)) return null;
  for (const k of ROW_IDS) {
    const v = (x as Record<string, unknown>)[k];
    if (typeof v === "string" || typeof v === "number") return `${k}=${v}`;
  }
  return null;
}
const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x);
const isScalar = (x: unknown) => x == null || typeof x !== "object";
/** 行的名字：修改记录里拿它让人认出是哪一行 */
function rowName(x: unknown): string | undefined {
  if (!isObj(x)) return undefined;
  for (const k of ["name", "label", "title", "nameI18n", "titleI18n"]) {
    const v = x[k];
    if (typeof v === "string" && v.trim()) return v.trim().slice(0, 40);
    if (isObj(v)) { const s = Object.values(v).find((y) => typeof y === "string" && y.trim()); if (typeof s === "string") return s.trim().slice(0, 40); }
  }
  return undefined;
}
/** 日志里的值截短：一整张表塞进一条记录没人看 */
function brief(v: unknown): unknown {
  if (v === undefined) return undefined;
  const s = JSON.stringify(v);
  return s.length > 120 ? s.slice(0, 117) + "…" : v;
}

interface WalkCtx { out: Change[]; limit: number; cut: (v: unknown) => unknown }

function walk(block: string, path: string, a: unknown, b: unknown, ctx: WalkCtx, row?: string) {
  const { out, limit, cut } = ctx;
  if (out.length >= limit || same(a, b)) return;
  // 标量数组（星级倍率、各栋 1 星金额）整体算一个值：拆成 [0] [1] [2] 人看不懂
  const scalarList = (x: unknown) => Array.isArray(x) && x.every(isScalar);
  if (scalarList(a) && scalarList(b)) { out.push({ block, path, from: cut(a), to: cut(b), ...(row ? { row } : {}) }); return; }
  if (Array.isArray(a) && Array.isArray(b)) {
    // 按 id / code 认行；有重复的认不准（Map 会把同号的两行并成一行、漏报改动），退回按下标
    const uniq = (xs: unknown[]) => xs.every((x) => rowId(x)) && new Set(xs.map(rowId)).size === xs.length;
    if (uniq(a) && uniq(b)) {
      const am = new Map(a.map((x) => [rowId(x)!, x]));
      const bm = new Map(b.map((x) => [rowId(x)!, x]));
      const top = !path; // 只有块的顶层行才记行名；更深的嵌套沿用外层行名
      for (const [id, x] of am) if (!bm.has(id)) out.length < limit && out.push({ block, path: `${path}[${id}]`, op: "remove", from: cut(x), ...((top ? rowName(x) : row) ? { row: top ? rowName(x) : row } : {}) });
      for (const [id, y] of bm) {
        if (!am.has(id)) { if (out.length < limit) out.push({ block, path: `${path}[${id}]`, op: "add", to: cut(y), ...((top ? rowName(y) : row) ? { row: top ? rowName(y) : row } : {}) }); }
        else walk(block, `${path}[${id}]`, am.get(id), y, ctx, top ? rowName(y) ?? rowName(am.get(id)) : row);
      }
      return;
    }
    const n = Math.max(a.length, b.length);
    for (let i = 0; i < n; i++) {
      if (i >= a.length) { if (out.length < limit) out.push({ block, path: `${path}[${i}]`, op: "add", to: cut(b[i]), ...(row ? { row } : {}) }); }
      else if (i >= b.length) { if (out.length < limit) out.push({ block, path: `${path}[${i}]`, op: "remove", from: cut(a[i]), ...(row ? { row } : {}) }); }
      else walk(block, `${path}[${i}]`, a[i], b[i], ctx, row);
    }
    return;
  }
  if (isObj(a) && isObj(b)) {
    for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) walk(block, path ? `${path}.${k}` : k, a[k], b[k], ctx, row);
    return;
  }
  out.push({ block, path, from: cut(a), to: cut(b), ...(row ? { row } : {}) });
}

/** 一次保存改了什么：逐块逐字段列出旧值→新值，最多 limit 条（审计 detail 列放得下） */
export function summarizeChanges(base: Snap, next: Snap, limit = 40): { changes: Change[]; truncated: boolean } {
  const a = base as Record<string, unknown>, b = next as Record<string, unknown>;
  const out: Change[] = [];
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) walk(k, "", a[k], b[k], { out, limit: limit + 1, cut: brief });
  return { changes: out.slice(0, limit), truncated: out.length > limit };
}

/**
 * 两份快照的完整差异（版本对比用）：与修改记录同一套认行规则——行按 id / code 认，
 * 中间插一行不会让后面每一行都显示成「改了」。不截值、不限条数
 */
export function diffBlocks(a: Record<string, unknown>, b: Record<string, unknown>, blocks: readonly string[]): Change[] {
  const out: Change[] = [];
  for (const k of blocks) walk(k, "", a[k], b[k], { out, limit: Infinity, cut: (v) => v });
  return out;
}

/** 失败后的重试间隔：2s 起翻倍，封顶 30s */
export const retryDelay = (attempt: number) => Math.min(2000 * 2 ** Math.max(0, attempt - 1), 30000);

type RecSnap = {
  puzzles: Array<{ localKey: string; puzzleId: string | null; seq: number }>;
  pets: Array<{ petId: string; persisted: boolean }>;
} & AssignedBlocks;
/**
 * 建行请求可能已经在服务端提交、只是回包丢了：按读回的服务端快照**精确**认领，不猜。
 * - 萌宠：编号是主键，服务端有这个编号就是建成了
 * - 拼图：本地键就是建行时带上的 uid，服务端那一行的本地键（= uid）对得上就是这一张
 * 关卡不在这里：它有 uid，合并（merge3）自己认得出，服务端还会拒掉同一 uid 的重复建关
 * 认不出的行原样是新行——不会把别人刚建的同 idol、同图的拼图认成自己的
 *
 * sentPets：建行请求**发出去了、没拿到回包**的萌宠编号。萌宠编号是运营手填的，服务端有同号的
 * 也可能是别人同时建的那一只；只认我发过的，没发过的留着「未落库」，下一轮建行时服务端拒重号、报出来。
 * 不给就按旧口径全认（单测与无从得知的调用方）
 */
export function reconcileLostCreates<S extends RecSnap>(live: S, server: S, sentPets?: ReadonlySet<string>): S {
  const serverPets = new Set(server.pets.map((p) => p.petId).filter((id) => !sentPets || sentPets.has(id)));
  const byKey = new Map(server.puzzles.filter((p) => p.puzzleId != null).map((p) => [p.localKey, p]));
  // 只补身份（id / 序号 / 已落库），内容一个字不动：服务端那一行之后可能已经被别人改过，
  // 拿它整行顶上会把别人的改动当成我发的。萌宠进状态前已规范成服务端的样子（normalizePet），补上 persisted 就对得上
  const puzzles = live.puzzles.map((x) => {
    if (x.puzzleId != null) return x;
    const hit = byKey.get(x.localKey);
    return hit ? { ...x, puzzleId: hit.puzzleId, seq: hit.seq } : x;
  });
  const pets = live.pets.map((x) => (!x.persisted && serverPets.has(x.petId) ? { ...x, persisted: true } : x));
  const out: S = { ...live, puzzles, pets };
  // 相册三块同拼图：本地键就是建行时带上的 uid，服务端那一行本地键对得上就是这一行；只补 ID，内容不动
  for (const [block, idKey] of ASSIGNED_ID_BLOCKS) {
    const mine = live[block] as AssignedRow[] | undefined;
    const theirs = server[block] as AssignedRow[] | undefined;
    if (!mine || !theirs) continue;
    const ids = new Map(theirs.filter((x) => x[idKey] != null).map((x) => [x.localKey, x[idKey]]));
    (out as AssignedBlocks)[block] = mine.map((x) => (x[idKey] == null && ids.has(x.localKey) ? { ...x, [idKey]: ids.get(x.localKey) } : x));
  }
  return out;
}


/** 表单草稿用（useLiveDraft） */
const sameJson = (a: unknown, b: unknown) => canon(a) === canon(b);
/** 三方合并（按顶层字段）：ours 相对 base 没动过的字段取 theirs。不是对象就看 ours 动没动 */
export function mergeFields<D>(base: D, ours: D, theirs: D): D {
  if (sameJson(ours, base)) return theirs;
  const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x);
  if (!isObj(base) || !isObj(ours) || !isObj(theirs)) return ours;
  const out: Record<string, unknown> = { ...theirs };
  for (const k of new Set([...Object.keys(ours), ...Object.keys(base)])) {
    if (!sameJson(ours[k], base[k])) { if (k in ours) out[k] = ours[k]; else delete out[k]; }
  }
  return out as D;
}


/*
 * ─── 协同编辑：字段级三方合并 ──────────────────────────────────────────────
 * 别人存了、我手上也有没存的改动：以「上一次的服务端」为 base，把我的（mine）和服务端现在的（theirs）合起来。
 * - 只有一边改的取那一边；两边改成一样的取哪边都一样
 * - 对象按字段递归；行数组按行身份（uid / key / code / petId / localKey / puzzleId / … / id）逐行递归，
 *   新增的行插在它在我这边的前一行后面，没有前一行就放最前
 * - 两边改了同一个字段成不同的值：**我的为准**，记一笔冲突（位置），界面提示；修改记录里两笔都在
 * - 一边删了、另一边改了同一行：改的那边赢（删除被撤回）；我删了、他没动：删
 * 关卡要先有 uid 才能这样合：id 是位置，插一关后面全变
 */
export const ROW_KEYS = ["uid", "key", "code", "petId", "localKey", "puzzleId", "packId", "rewardId", "nodeId", "id", "seq", "star", "level",
  // 以下几张表的行只有这些身份字段（战阶 / 道具卡 / 玩法 / 事件类型 / 头像框 / 萌宠小镇槽位 / 领养点）。
  // 放在最后：前面任何一个认得出的表不受影响；以前这几张认不出行，并发时整张表以「我的」为准，别人新加的行整行丢掉
  "stage", "cardId", "mode", "type", "frameId", "slot", "point"];
/**
 * 按块指定身份字段。卡包的 code 是运营能改的展示编号，两个人同时新建会拿到同一个建议值：
 * 按 code 认会把两包认成一包、之后把对方那包删掉。id 新建时就是随机的（lib/card.ts newPack），按它认
 */
const BLOCK_ROW_KEY: Record<string, string> = { itemPacks: "id", idolPacks: "id" };
const isPlain = (x: unknown): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x);
const eq = (a: unknown, b: unknown) => canon(a) === canon(b);

/**
 * 行身份：返回「怎么认一行」的函数和它在冲突位置里的写法，认不出返回 null（整体比）。
 * - 拼图：落库了认 puzzleId，没落库认 localKey（服务端读回的行 localKey = puzzleId，
 *   本地新建的是 local_N——只认 localKey 会把同一张拼图认成两张）
 * - 其余按 ROW_KEYS 的先后取第一个「每行都有、每份里不重号」的字段
 */
function rowIdentity(lists: unknown[][], path = ""): { id: (r: Record<string, unknown>) => string; key: string } | null {
  const rows = lists.flat();
  if (!rows.length || !rows.every(isPlain)) return null;
  const fixed = BLOCK_ROW_KEY[path.replace(/^\./, "")];
  if (fixed && rows.every((r) => typeof r[fixed] === "string" || typeof r[fixed] === "number")
    && lists.every((l) => new Set(l.map((r) => (r as Record<string, unknown>)[fixed])).size === l.length)) {
    return { id: (r) => String(r[fixed]), key: fixed };
  }
  if (rows.every((r) => "puzzleId" in r && "localKey" in r)) {
    const id = (r: Record<string, unknown>) => (r.puzzleId != null ? String(r.puzzleId) : `local:${String(r.localKey)}`);
    if (lists.every((l) => new Set((l as Record<string, unknown>[]).map(id)).size === l.length)) return { id, key: "puzzleId" };
  }
  for (const k of ROW_KEYS) {
    if (!rows.every((r) => typeof r[k] === "string" || typeof r[k] === "number")) continue;
    // 每一份里不能重号，否则认不出行
    if (lists.every((l) => new Set(l.map((r) => (r as Record<string, unknown>)[k])).size === l.length)) return { id: (r) => String(r[k]), key: k };
  }
  return null;
}

export function merge3(base: unknown, mine: unknown, theirs: unknown, path = "", conflicts: string[] = []): unknown {
  if (eq(mine, base)) return theirs;
  if (eq(theirs, base)) return mine;
  if (eq(mine, theirs)) return mine;
  if (Array.isArray(mine) && Array.isArray(theirs)) {
    const b = Array.isArray(base) ? base : [];
    /*
     * 一关里的建筑按序号（第几栋）认：它的 code 里带着关卡号（「2-building-1」），
     * 别人在前面插一关后同一栋就变成了「3-building-1」，按 code 认会把同一栋认成两栋
     */
    if (/\.buildings$/.test(path)) {
      if (b.length === mine.length && mine.length === theirs.length) return mine.map((m, i) => merge3(b[i], m, theirs[i], `${path}[${i}]`, conflicts));
      conflicts.push(path);
      return mine;
    }
    const ident = rowIdentity([b, mine, theirs], path);
    if (ident) return mergeRows(b as Record<string, unknown>[], mine as Record<string, unknown>[], theirs as Record<string, unknown>[], ident, path, conflicts);
  }
  if (isPlain(mine) && isPlain(theirs)) {
    const b = isPlain(base) ? base : {};
    const out: Record<string, unknown> = {};
    for (const k of new Set([...Object.keys(b), ...Object.keys(mine), ...Object.keys(theirs)])) {
      const v = merge3(b[k], mine[k], theirs[k], path ? `${path}.${k}` : k, conflicts);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  conflicts.push(path);
  return mine;
}

function mergeRows(base: Record<string, unknown>[], mine: Record<string, unknown>[], theirs: Record<string, unknown>[], ident: { id: (r: Record<string, unknown>) => string; key: string }, path: string, conflicts: string[]) {
  const { id, key } = ident;
  const bm = new Map(base.map((r) => [id(r), r])), mm = new Map(mine.map((r) => [id(r), r])), tm = new Map(theirs.map((r) => [id(r), r]));
  const out: Record<string, unknown>[] = [];
  for (const t of theirs) {
    const k = id(t), b = bm.get(k), m = mm.get(k);
    const at = `${path}[${key}=${k}]`;
    if (m) { out.push(merge3(b, m, t, at, conflicts) as Record<string, unknown>); continue; }
    if (!b) { out.push(t); continue; }              // 他新加的
    if (eq(t, b)) continue;                          // 我删了、他没动：删
    conflicts.push(at); out.push(t);                 // 我删了、他改了：保留他改的
  }
  // 我这边有、结果里还没有的：我新加的，或者他删了而我改了的（改的赢）
  for (let i = 0; i < mine.length; i++) {
    const m = mine[i], k = id(m);
    if (tm.has(k)) continue;
    const b = bm.get(k);
    if (b) { if (eq(m, b)) continue; conflicts.push(`${path}[${key}=${k}]`); }
    // 插在它在我这边的前一行之后
    let pos = 0;
    for (let j = i - 1; j >= 0; j--) { const idx = out.findIndex((r) => id(r) === id(mine[j])); if (idx >= 0) { pos = idx + 1; break; } }
    out.splice(pos, 0, m);
  }
  return out;
}

/**
 * 回包丢失后的对账基线修正：请求可能已经提交、只是没拿到回执。
 * 读回里凡是「和我发出去的一模一样」的地方，就当作是我写进去的（真是别人写成同样的值，结果也一样），
 * 基线取我发的；其余沿用已确认的基线。这样保存期间我把某个值又改回原样，
 * 合并时它是「我改了」，不会被读回里我自己刚写进去的值盖掉。
 * 行数组按行身份逐行比；对象按字段比；其余整体比
 */
export function refineBase(base: unknown, sent: unknown, server: unknown, path = ""): unknown {
  if (eq(server, sent)) return sent;
  if (Array.isArray(base) && Array.isArray(sent) && Array.isArray(server)) {
    const ident = rowIdentity([base, sent, server], path);
    if (!ident) return base;
    const { id } = ident;
    const sm = new Map((sent as Record<string, unknown>[]).map((r) => [id(r), r]));
    const vm = new Map((server as Record<string, unknown>[]).map((r) => [id(r), r]));
    const out: Record<string, unknown>[] = [];
    for (const b of base as Record<string, unknown>[]) {
      const k = id(b), s = sm.get(k), v = vm.get(k);
      if (!s && !v) continue;                                // 我删了、服务端也没了：删成了
      out.push(s && v ? (refineBase(b, s, v, path) as Record<string, unknown>) : b);
    }
    for (const s of sent as Record<string, unknown>[]) {
      const k = id(s);
      if (out.some((r) => id(r) === k)) continue;
      /*
       * 我新加的、服务端有了这个身份（uid / 编号，先认领过）：建成了。
       * - 萌宠 / 拼图是整行建的：基线取我发的那一行（服务端那行之后可能被别人改过，按内容比认不出）
       * - 关卡建关只写一部分字段（实际总造价、底图、摆位等由紧跟的补丁写），补丁可能没发出去：
       *   基线取服务端那一行，我这边与它不同的字段都还算没存，下一轮补上
       */
      const v = vm.get(k);
      if (v) out.push(path === ".levels" ? v : s);
    }
    return out;
  }
  if (isPlain(base) && isPlain(sent) && isPlain(server)) {
    const out: Record<string, unknown> = { ...base };
    for (const k of new Set([...Object.keys(base), ...Object.keys(sent)])) {
      if (k in sent && k in server) out[k] = refineBase(base[k], sent[k], server[k], `${path}.${k}`);
    }
    return out;
  }
  return base;
}

/**
 * 按关号做键的块（竞拍分期 auctions）换成另一份快照的关号：先按这份快照自己的关卡认出 uid，再找 uid 在目标里的编号。
 * 别人插关或拖动改编号以后，同一个关号已经不是同一关；三方合并之前把 base / mine 都换到服务端现在的编号上，
 * 合出来的改动才落在同一关上（2026-10-27）。目标里没有这一关（删了 / 我新加还没存）就保留原键
 */
export function rekeyByLevelUid<V>(map: Record<number, V>, own: Array<{ id: number; uid: string }>, target: Array<{ id: number; uid: string }>): Record<number, V> {
  const uidAt = new Map(own.map((l) => [l.id, l.uid]));
  const idOf = new Map(target.map((l) => [l.uid, l.id]));
  const out: Record<number, V> = {};
  for (const [k, v] of Object.entries(map)) {
    const uid = uidAt.get(Number(k));
    const to = uid ? idOf.get(uid) : undefined;
    out[to ?? Number(k)] = v;
  }
  return out;
}
